import React from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { Quizzes } from '../../../redux/quiz/types'
import { useContextAwareValues } from './ContextAwareText'

/* ContextAwareText, typed out a character at a time. The full text is laid out
   underneath (transparent, but still what screen readers read) so the container
   is its final size from the first frame and nothing below it moves while the
   typed copy, drawn on top, catches up. */

/* Short messages type at MS_PER_CHAR; longer ones speed up (by the square root
   of how much longer they are) so they don't keep the user waiting, down to
   MIN_MS_PER_CHAR. ~40 chars: 14ms each, ~150 chars: ~7ms each. */
const MS_PER_CHAR = 14
const MIN_MS_PER_CHAR = 5
const SHORT_MESSAGE_CHARS = 40

const msPerChar = (total: number) =>
  Math.max(MIN_MS_PER_CHAR, MS_PER_CHAR * Math.sqrt(Math.min(1, SHORT_MESSAGE_CHARS / total)))

const Container = styled.span`
  position: relative;
  display: block;
`

const Ghost = styled.span`
  display: block;
  opacity: 0;
`

const Typed = styled.span`
  position: absolute;
  inset: 0;
`

type Props = {
  text: string
  quiz?: Quizzes
  additionalTokens?: Record<string, any>
  // Set to show the whole text at once, e.g. when the user taps to skip ahead.
  complete?: boolean
  onComplete?: () => void
}

// <br /> and other childless elements count as one character.
const length = (node: React.ReactNode): number => {
  if (typeof node === 'string' || typeof node === 'number') return String(node).length
  if (Array.isArray(node)) return node.reduce((sum: number, child) => sum + length(child), 0)
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return node.props.children === undefined ? 1 : length(node.props.children)
  }
  return 0
}

// The first `count` characters of a formatted message, keeping its markup.
const truncate = (node: React.ReactNode, budget: { left: number }): React.ReactNode => {
  if (budget.left <= 0) return null
  if (typeof node === 'string' || typeof node === 'number') {
    const text = String(node)
    const typed = text.slice(0, budget.left)
    budget.left -= text.length
    return typed
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => <React.Fragment key={i}>{truncate(child, budget)}</React.Fragment>)
  }
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    if (node.props.children === undefined) {
      budget.left -= 1
      return node
    }
    return React.cloneElement(node, undefined, truncate(node.props.children, budget))
  }
  return node
}

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

export const TypewriterText: React.FC<Props> = ({ text, quiz, additionalTokens, complete, onComplete }) => {
  const intl = useIntl()
  const values = useContextAwareValues(quiz, additionalTokens)
  const message = intl.formatMessage({ id: text, defaultMessage: text }, values) as React.ReactNode
  const total = length(message)
  const [count, setCount] = React.useState(() => (prefersReducedMotion() ? total : 0))
  const done = complete || count >= total

  /* Driven by elapsed time rather than one character per tick: below ~16ms a
     tick per character would be capped by the browser's timer resolution. */
  React.useEffect(() => {
    if (done) return
    const start = Date.now()
    const speed = msPerChar(total)
    const timer = window.setInterval(() => setCount(Math.floor((Date.now() - start) / speed)), 16)
    return () => window.clearInterval(timer)
  }, [done])

  React.useEffect(() => {
    if (done) onComplete?.()
  }, [done])

  return (
    <Container>
      <Ghost>{message}</Ghost>
      <Typed aria-hidden>{done ? message : truncate(message, { left: count })}</Typed>
    </Container>
  )
}
